import { changeSalesPriceBasis } from "utils/sales_price_basis"
import { preventImplicitSubmit } from "../../../../../app/javascript/keyboard/form_keys"
import { fetchVariantDetails } from "utils/variant_details"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["form", "itemTemplate", "itemsContainer", "itemsPanel", "subtotal", "deliveryMethod", "deliveryFee", "tax", "total", "itemVariantCombobox"]
    static values = { itemVariantsUrl: String, itemVariantDetailsUrl: String, readonly: Boolean, newRecord: Boolean, deliveryFeeAmount: Number, freeShippingThreshold: Number, fixedDeliveryFee: Number, lockDeliveryFee: Boolean, currency: String }

    changePriceBasis(event) {
        const row = event.target.closest(".item-container")
        changeSalesPriceBasis(row, event.target, "input[name$='[estimated_weight]']")
        this.calculateOrder()
    }

    connect() {
        this.itemVariantsById = {}
        this.itemContent = this.itemTemplateTarget.innerHTML
        this.clientChangeTimeout = null
        this.lastClientId = this.clientId() || ""

        this.formTarget.addEventListener("keydown", preventImplicitSubmit)


        setTimeout(() => {
            this.clientSelectInput = this.formTarget.querySelector("input[id='order_client_id_hw_combobox']")
            this.clientSelectInput?.focus()
            if (this.clientIdPresent()) {
                this.initializeExistingItems()
            }
            this.calculateOrder()
        }, 10)
    }

    disconnect() {
        this.clearPendingClientChange()
    }

    addItem(event) {
        if (this.readonlyValue) return
        if (event) event.preventDefault()
        const content = this.itemContent.replace(/NEW_RECORD/g, new Date().getTime())
        this.itemsContainerTarget.insertAdjacentHTML("beforeend", content)
        const lastItem = this.itemsContainerTarget.querySelector("li.item-container:last-child")
        this.configureItemVariantCombobox(lastItem)
        this.toggleAllocationPanel(lastItem, null)
        const firstInput = lastItem.querySelector("input")
        firstInput.focus()
    }

    removeItem(event) {
        if (this.readonlyValue) return
        event.preventDefault()
        const wrapper = event.target.closest(".item-container")
        wrapper.remove()
    }

    clearItems() {
        if (this.readonlyValue) return
        this.itemsContainerTarget.innerHTML = ""
    }

    changeClient(event) {
        if (this.readonlyValue) return
        if (event) event.preventDefault()

        const nextClientId = this.clientId() || ""
        const clientChanged = nextClientId !== this.lastClientId

        this.lastClientId = nextClientId
        this.itemVariantsById = {}
        this.clearPendingClientChange()

        if (clientChanged) this.clearItems()

        if (!nextClientId) {
            this.itemsPanelTarget.classList.add("d-none")
            return
        }

        this.clientChangeTimeout = setTimeout(() => {
            if ((this.clientId() || "") !== nextClientId) return

            this.itemsPanelTarget.classList.remove("d-none")
            this.ensureSingleBlankItem()
            this.clientChangeTimeout = null
        }, 10)
    }

    clientIdPresent() {
        return Boolean(this.clientId())
    }

    clientId() {
        return this.formTarget.querySelector("input[name='order[client_id]']")?.value
    }

    selectVariant(event) {
        if (this.readonlyValue) return
        if (event) event.preventDefault()
        setTimeout(() => {
            const item = event.target.closest("li.item-container")
            const variantInput = item.querySelector("input[name$='[item_variant_id]']")
            const variantID = variantInput.value
            const unitPriceInput = item.querySelector("input[name$='[unit_price]']")
            const previousVariantId = item.dataset.variantId
            const variantChanged = previousVariantId && previousVariantId !== variantID

            if (!variantID) {
                unitPriceInput.value = ""
                item.dataset.variantId = ""
                this.toggleAllocationPanel(item, null)
                return
            }

            if (variantChanged) {
                this.clearAllocationRows(item)
                const panel = item.querySelector("[data-allocation-panel]")
                if (panel) panel.dataset.autoManaged = "true"
            }

            this.ensureVariantDetails(variantID)
                .then(detailedVariant => {
                    if (!detailedVariant) return
                    item.dataset.variantId = String(detailedVariant.id)
                    unitPriceInput.value = detailedVariant.price
                    this.configureCatchWeight(item, detailedVariant, true)
                    unitPriceInput.dispatchEvent(new Event("change"))
                    this.toggleAllocationPanel(item, detailedVariant)
                    this.updateAllocationSummary(item)
                    if (item.parentElement.lastElementChild === item) this.addItem()
                })
                .catch(error => console.error(error))
        }, 10)
    }

    addAllocation(event) {
        if (this.readonlyValue) return
        event.preventDefault()
        const item = event.target.closest(".item-container")
        this.disableAutoAllocationForItem(item)
        this.appendAllocationRow(item)
        this.updateAllocationSummary(item)
    }

    removeAllocation(event) {
        if (this.readonlyValue) return
        event.preventDefault()
        const item = event.target.closest(".item-container")
        this.disableAutoAllocationForItem(item)
        const row = event.target.closest("[data-allocation-row]")
        const destroyInput = row.querySelector("input[data-allocation-destroy]")
        const persistedIdInput = row.querySelector("input[name$='[id]']")

        if (persistedIdInput && persistedIdInput.value.length > 0) {
            destroyInput.value = 1
            row.classList.add("d-none")
        } else {
            row.remove()
        }

        this.updateAllocationSummary(item)
    }

    disableAutoAllocation(event) {
        if (this.readonlyValue) return
        const item = event.target.closest(".item-container")
        this.disableAutoAllocationForItem(item)
    }

    handleAllocationSelectionChange(event) {
        if (this.readonlyValue) return

        this.disableAutoAllocation(event)
        this.storeSelectedAllocationLabel(event.target)
        this.updateAllocationSummary(event.target.closest(".item-container"))
    }

    calculateOrder(event) {
        if (event) event.preventDefault()
        if (event?.target?.name?.endsWith("[quantity]")) {
            const item = event.target.closest("li.item-container")
            const estimate = item?.querySelector("input[name$='[estimated_weight]']")
            if (estimate && item.dataset.estimatedWeightPerUnit) estimate.value = (Number(event.target.value) * Number(item.dataset.estimatedWeightPerUnit)).toFixed(4)
        }
        const items = this.itemsContainerTarget.querySelectorAll("li.item-container")
        const subtotal = [...items].reduce((total, item) => {
            const unitPriceInput = item.querySelector("input[name$='[unit_price]']")
            const quantityInput = item.querySelector("input[name$='[quantity]']")
            const unitPrice = Number(unitPriceInput.value)
            const catchWeight = item.querySelector("[data-sales-price-basis]")?.value === "weight"
            const quantity = Number(catchWeight ? item.querySelector("input[name$='[estimated_weight]']")?.value : quantityInput.value)
            return total + unitPrice * quantity
        }, 0)
        const deliveryFee = this.currentDeliveryFee(subtotal)
        const tax = 0
        const total = subtotal + deliveryFee + tax
        this.subtotalTarget.innerText = this.formatCurrency(subtotal)
        this.deliveryFeeTarget.innerText = this.formatCurrency(deliveryFee)
        this.taxTarget.innerText = this.formatCurrency(tax)
        this.totalTarget.innerText = this.formatCurrency(total)
    }

    currentDeliveryFee(subtotal) {
        if (this.lockDeliveryFeeValue) return this.fixedDeliveryFeeValue || 0
        const selectedMethod = this.deliveryMethodTarget?.selectedOptions?.[0]
        if (selectedMethod?.dataset.pickup === "true") return 0

        const rate = Number(this.element.querySelector('select[name$="[currency]"]')?.selectedOptions?.[0]?.dataset.exchangeRate) || 1
        const configuredFee = (Number(selectedMethod?.dataset.deliveryFeeAmount ?? this.deliveryFeeAmountValue) || 0) / rate
        const freeShippingThreshold = (Number(selectedMethod?.dataset.freeDeliveryThreshold ?? this.freeShippingThresholdValue) || 0) / rate

        if (configuredFee <= 0) return 0
        if (freeShippingThreshold > 0 && subtotal >= freeShippingThreshold) return 0

        return configuredFee
    }

    formatCurrency(amount) {
        return Intl.NumberFormat("en-AU", { style: "currency", currency: this.element.querySelector('select[name$="[currency]"]')?.value || this.currencyValue || "AUD", currencyDisplay: "code" }).format(amount)
    }

    clearPendingClientChange() {
        if (!this.clientChangeTimeout) return

        clearTimeout(this.clientChangeTimeout)
        this.clientChangeTimeout = null
    }

    ensureSingleBlankItem() {
        const items = this.itemsContainerTarget.querySelectorAll("li.item-container")
        if (items.length === 0) {
            this.addItem()
            return
        }

        const blankItems = [...items].filter(item => this.itemIsBlank(item))
        if (blankItems.length <= 1) return

        blankItems.slice(1).forEach(item => item.remove())
    }

    itemIsBlank(item) {
        const variantInput = item.querySelector("input[name$='[item_variant_id]']")
        const quantityInput = item.querySelector("input[name$='[quantity]']")
        const unitPriceInput = item.querySelector("input[name$='[unit_price]']")

        return !variantInput?.value && !quantityInput?.value && !unitPriceInput?.value
    }

    initializeExistingItems() {
        const detailRequests = []

        this.itemsContainerTarget.querySelectorAll("li.item-container").forEach(item => {
            this.configureItemVariantCombobox(item)
            const variantInput = item.querySelector("input[name$='[item_variant_id]']")
            if (!variantInput || variantInput.value.length === 0) return
            item.dataset.variantId = variantInput.value

            detailRequests.push(
                this.ensureVariantDetails(variantInput.value).then(detailedVariant => {
                    this.configureCatchWeight(item, detailedVariant)
                    this.toggleAllocationPanel(item, detailedVariant)
                    this.updateAllocationSummary(item)
                    this.calculateOrder()
                })
            )
        })

        return Promise.all(detailRequests)
    }

    toggleAllocationPanel(item, variant) {
        const panel = item.querySelector("[data-allocation-panel]")
        const details = item.querySelector("details.order-line-sources")
        if (!panel) return
        if (this.readonlyValue) return

        if (!variant || !variant.requires_stock_entry_allocation) {
            panel.classList.add("d-none")
            panel.dataset.autoManaged = "false"
            if (details) {
                details.classList.add("d-none")
                details.open = false
            }
            this.clearAllocationRows(item)
            this.updateAllocationSummary(item)
            return
        }

        panel.classList.remove("d-none")
        if (details) details.classList.remove("d-none")
        if (details && this.newRecordValue) details.open = true
        this.populateAllocationOptions(item, variant.stock_entries || [])

        if (this.visibleAllocationRows(item).length === 0) {
            this.applyAllocationSuggestions(item, variant)
        }

        this.updateAllocationSummary(item)
    }

    configureCatchWeight(item, variant, reset = false) {
        const fields = item.querySelector("[data-catch-weight-fields]")
        const flag = item.querySelector("[data-catch-weight-flag]")
        const input = item.querySelector("input[name$='[estimated_weight]']")
        if (!fields || !flag || !input) return
        const tracked = Boolean(variant?.catch_weight)
        const basis = item.querySelector("[data-sales-price-basis]")
        if (basis) {
            if (reset || !basis.value) basis.value = variant?.sales_price_basis || (tracked ? "weight" : "unit")
            basis.dataset.previousBasis = basis.value
            basis.disabled = this.readonlyValue || !tracked
        }
        flag.value = String(tracked)
        fields.classList.toggle("d-none", !tracked)
        input.disabled = this.readonlyValue || !tracked
        const quantity = Number(item.querySelector("input[name$='[quantity]']")?.value || 0)
        const perUnit = !reset && Number(input.value) > 0 && quantity > 0 ? Number(input.value) / quantity : Number(variant?.estimated_weight_per_unit || 0)
        item.dataset.estimatedWeightPerUnit = String(perUnit)
        if (reset || !Number(input.value)) input.value = (quantity * perUnit).toFixed(4)
    }

    syncAllocationSuggestions(event) {
        if (this.readonlyValue) return
        const item = event.target.closest(".item-container")
        if (!item) return

        const variant = this.variantForItem(item)
        if (!variant || !variant.requires_stock_entry_allocation) return

        const panel = item.querySelector("[data-allocation-panel]")
        const hasSelectedAllocation = this.visibleAllocationRows(item).some(row => {
            const select = row.querySelector("select.stock-entry-select")
            return select && select.value.length > 0
        })

        if (panel.dataset.autoManaged === "true" || !hasSelectedAllocation) {
          this.applyAllocationSuggestions(item, variant)
        }
    }

    clearAllocationRows(item) {
        if (this.readonlyValue) return
        this.allocationRowsContainer(item).querySelectorAll("[data-allocation-row]").forEach(row => {
            const destroyInput = row.querySelector("input[data-allocation-destroy]")
            const persistedIdInput = row.querySelector("input[name$='[id]']")

            if (persistedIdInput && persistedIdInput.value.length > 0) {
                destroyInput.value = 1
                row.classList.add("d-none")
            } else {
                row.remove()
            }
        })

        this.updateAllocationSummary(item)
    }

    appendAllocationRow(item, stockEntries = null) {
        if (this.readonlyValue) return
        const template = item.querySelector("template[data-allocation-template]")
        if (!template) return

        const content = template.innerHTML.replace(/NEW_ALLOCATION/g, `${Date.now()}${Math.floor(Math.random() * 1000)}`)
        this.allocationRowsContainer(item).insertAdjacentHTML("beforeend", content)
        const row = this.allocationRowsContainer(item).querySelector("[data-allocation-row]:last-child")
        const variant = this.variantForItem(item)
        this.populateAllocationOptions(item, stockEntries || variant?.stock_entries || [])
        this.updateAllocationSummary(item)
        return row
    }

    applyAllocationSuggestions(item, variant) {
        if (this.readonlyValue) return
        const panel = item.querySelector("[data-allocation-panel]")
        if (!panel) return

        const strategy = variant.allocation_strategy || "manual"
        if (strategy === "manual") {
            panel.dataset.autoManaged = "false"
            if (this.visibleAllocationRows(item).length === 0) this.appendAllocationRow(item, variant.stock_entries || [])
            this.updateAllocationSummary(item)
            return
        }

        const quantityInput = item.querySelector("input[name$='[quantity]']")
        const quantity = Number(quantityInput?.value || 0)
        if (quantity <= 0) {
            panel.dataset.autoManaged = "true"
            this.clearAllocationRows(item)
            this.appendAllocationRow(item, variant.stock_entries || [])
            this.updateAllocationSummary(item)
            return
        }

        const suggestions = this.buildAllocationSuggestions(variant.stock_entries || [], quantity)
        panel.dataset.autoManaged = "true"
        this.clearAllocationRows(item)

        if (suggestions.length === 0) {
            this.appendAllocationRow(item, variant.stock_entries || [])
            this.updateAllocationSummary(item)
            return
        }

        suggestions.forEach(suggestion => {
            const row = this.appendAllocationRow(item, variant.stock_entries || [])
            const select = row.querySelector("select.stock-entry-select")
            const allocationQuantity = row.querySelector("input[name$='[quantity]']")
            if (select) {
                select.value = String(suggestion.stockEntryId)
                this.storeSelectedAllocationLabel(select)
            }
            if (allocationQuantity) allocationQuantity.value = suggestion.quantity
        })

        this.updateAllocationSummary(item)
    }

    buildAllocationSuggestions(stockEntries, requiredQuantity) {
        let remaining = requiredQuantity
        const suggestions = []

        stockEntries.forEach(stockEntry => {
            if (remaining <= 0) return

            const available = Number(stockEntry.available_quantity || 0)
            if (available <= 0) return

            const quantity = Math.min(available, remaining)
            suggestions.push({
                stockEntryId: stockEntry.id,
                quantity: quantity
            })
            remaining -= quantity
        })

        return suggestions
    }

    populateAllocationOptions(item, stockEntries) {
        item.querySelectorAll("select.stock-entry-select").forEach(select => {
            const currentValue = select.value
            const currentOption = select.options[select.selectedIndex]
            const currentLabel = select.dataset.selectedStockEntryLabel || (currentOption ? currentOption.textContent : null)
            select.innerHTML = ""

            const blankOption = document.createElement("option")
            blankOption.value = ""
            blankOption.textContent = "Select stock entry"
            select.appendChild(blankOption)

            stockEntries.forEach(stockEntry => {
                const option = document.createElement("option")
                option.value = stockEntry.id
                option.textContent = stockEntry.label
                if (String(stockEntry.id) === currentValue) option.selected = true
                select.appendChild(option)
            })

            if (currentValue && !stockEntries.find(stockEntry => String(stockEntry.id) === currentValue) && currentLabel) {
                const option = document.createElement("option")
                option.value = currentValue
                option.textContent = currentLabel
                option.selected = true
                select.appendChild(option)
            }

            this.storeSelectedAllocationLabel(select)
        })
    }

    allocationRowsContainer(item) {
        return item.querySelector("[data-allocation-rows]")
    }

    visibleAllocationRows(item) {
        return [...this.allocationRowsContainer(item).querySelectorAll("[data-allocation-row]")].filter(row => !row.classList.contains("d-none"))
    }

    variantForItem(item) {
        const variantInput = item.querySelector("input[name$='[item_variant_id]']")
        if (!variantInput || variantInput.value.length === 0) return null

        return this.variantById(variantInput.value)
    }

    ensureVariantDetails(variantId) {
        if (!this.clientId()) return Promise.resolve(null)

        const variant = this.variantById(variantId)
        if (variant?.stock_entries_loaded) return Promise.resolve(variant)

        const url = `${this.itemVariantsUrlValue}?client_id=${this.clientId()}&currency=${encodeURIComponent(this.element.querySelector("[name=\"order[currency]\"]")?.value || this.currencyValue)}`
        const requestKey = `${url}#${variantId}`
        this.variantDetailRequests ||= new Map()
        if (this.variantDetailRequests.has(requestKey)) return this.variantDetailRequests.get(requestKey)
        const request = fetchVariantDetails(url, variantId)
            .then(data => {
                this.itemVariantsById[data.id] = data
                return data
            })
            .finally(() => this.variantDetailRequests.delete(requestKey))
        this.variantDetailRequests.set(requestKey, request)
        return request
    }

    configureItemVariantCombobox(item) {
        const combobox = item.querySelector("[data-sales-new-order-target='itemVariantCombobox']")
        if (!combobox) return

        const fieldset = combobox.closest(".hw-combobox")
        if (!fieldset) return

        const serverDisabled = combobox.dataset.serverDisabled === "true"

        if (this.clientId()) {
            fieldset.dataset.hwComboboxAsyncSrcValue = `${this.itemVariantsUrlValue}?client_id=${encodeURIComponent(this.clientId())}`
            combobox.disabled = serverDisabled
        } else {
            fieldset.dataset.hwComboboxAsyncSrcValue = ""
            combobox.disabled = true
        }
    }

    variantById(variantId) {
        return this.itemVariantsById[Number(variantId)] || null
    }

    disableAutoAllocationForItem(item) {
        const panel = item?.querySelector("[data-allocation-panel]")
        if (panel) panel.dataset.autoManaged = "false"
    }

    storeSelectedAllocationLabel(select) {
        if (!select) return

        const selectedOption = select.options[select.selectedIndex]
        select.dataset.selectedStockEntryLabel = select.value && selectedOption ? selectedOption.textContent : ""
    }

    updateAllocationSummary(item) {
        if (!item) return

        const countBadge = item.querySelector("[data-allocation-count]")
        const summary = item.querySelector("[data-allocation-summary]")
        if (!countBadge || !summary) return

        const labels = this.visibleAllocationRows(item)
            .map(row => {
                const select = row.querySelector("select.stock-entry-select")
                if (!select || !select.value) return null

                return select.dataset.selectedStockEntryLabel || select.options[select.selectedIndex]?.textContent || null
            })
            .filter(Boolean)

        const count = labels.length
        countBadge.textContent = count > 0 ? `${count} ${count === 1 ? "source" : "sources"} selected` : "No sources"

        if (count > 0) {
            summary.textContent = labels.slice(0, 2).join(" | ")
            summary.classList.remove("d-none")
        } else {
            summary.textContent = ""
            summary.classList.add("d-none")
        }
    }
}
